import { z } from 'zod';
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX } from '../constants.js';
export const cuidSchema = z.string().min(1).max(64);
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254)
    .email();
/** Passwords: length is the only strength rule we enforce server-side (NIST-style). */
export const passwordSchema = z.string().min(10).max(200);
export const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a 6-digit hex colour such as #1A2B3C');
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be an ISO date (YYYY-MM-DD)');
export const hhmmSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be a 24h time (HH:MM)');
export const ianaTimezoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, 'Must be a valid IANA timezone');
export const currencySchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Must be a 3-letter ISO 4217 currency code');
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
/** Tenant subdomain slug: lowercase, dash-separated, no leading/trailing dash. */
/** A tenant slug becomes a DNS label, which caps at 63; 40 leaves room to read. */
export const TENANT_SLUG_MAX = 40;
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Use at least 3 characters')
    .max(TENANT_SLUG_MAX, `Use at most ${TENANT_SLUG_MAX} characters`)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use lowercase letters, digits and dashes, starting and ending with a letter or digit');
/**
 * Turns anything a human types into something that can be a DNS label.
 *
 * The console used to lowercase the raw input and send it, so pasting a company
 * name produced "ride fast transport ltd." and the server answered with a Zod
 * message about a field the operator was not looking at. Normalising as they type
 * means the value in the box is always the value that will be accepted.
 *
 * Accents are folded rather than dropped, so "Société" becomes "societe" and not
 * "socit".
 */
export function toTenantSlug(input) {
    const folded = input
        .normalize('NFD')
        // Combining marks, left behind by NFD once the base letter is separated.
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
    // Trim dashes BEFORE the cap and again after, or a cap that lands on a
    // separator leaves a trailing dash, which a DNS label may not end with.
    return folded
        .replace(/^-+|-+$/g, '')
        .slice(0, TENANT_SLUG_MAX)
        .replace(/-+$/g, '');
}
/** Slugs that would collide with platform hosts. */
export const RESERVED_SLUGS = [
    'www', 'api', 'admin', 'console', 'app', 'cdn', 'assets', 'mail', 'smtp',
    'status', 'docs', 'support', 'help', 'staging', 'dev', 'test',
];
export const tenantSlugSchema = slugSchema.refine((s) => !RESERVED_SLUGS.includes(s), 'This slug is reserved');
//# sourceMappingURL=common.js.map